import React, { useState, useRef, useEffect } from 'react'
import { toast, ToastContainer } from 'react-toastify';
import './GalleryImages.css';
import api from '../../api/axios.js'
import { API_IMAGE_BASE_URL } from '../../config/api';

const GalleryImages = () => {
  const fileInputRef = useRef(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  // Total images across the whole gallery (not just this page) — needed to
  // enforce the 6-image cap, which applies gallery-wide, not per page.
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    fetchGalleryImages(page);
  }, [page]);

  const fetchGalleryImages = async (pageNum = 1) => {
    setIsLoading(true);
    try {
      const res = await api.get(`/gallery-images?page=${pageNum}&limit=${limit}`);
      setGalleryImages(res.data?.data || []);
      // Backend returns pagination.totalItems/totalPages (see
      // getGalleryImages) — not a top-level `total`, which was always
      // undefined here and silently made totalPages fall back to 1.
      setTotalPages(res.data?.pagination?.totalPages || 1);
      setTotalItems(res.data?.pagination?.totalItems || 0);
    } catch (error) {
      console.error('Error fetching gallery images:', error);
      setMessage('Error fetching gallery images');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  };

  const MAX_GALLERY_UPLOAD = 6;

  const handleImageUpload = async (event) => {
    const files = Array.from(event.target.files);
    if (!files.length) return;

    // The cap is on the gallery's total, not just this selection — reject
    // as soon as existing + newly-selected would cross 6, even if that's
    // just 1 file on top of an already-full gallery.
    if (totalItems + files.length > MAX_GALLERY_UPLOAD) {
      const remaining = Math.max(MAX_GALLERY_UPLOAD - totalItems, 0);
      toast.error(
        remaining === 0
          ? `Gallery already has the maximum of ${MAX_GALLERY_UPLOAD} images. Delete one before uploading another.`
          : `Gallery already has ${totalItems} image(s). You can only add ${remaining} more (max ${MAX_GALLERY_UPLOAD} total).`
      );
      event.target.value = '';
      return;
    }

    const formData = new FormData();
    files.forEach(file => formData.append('images', file)); // backend expects 'images' field (upload.array)

    setIsLoading(true);
    try {
      await api.post('/admin/gallery-images/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      toast.success('Gallery image uploaded successfully!')
      setMessage('Images uploaded successfully!');
      fetchGalleryImages(page); // refresh list
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error uploading images:', error);
      setMessage(error.response?.data?.message || 'Error uploading images');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsLoading(false);
      event.target.value = '';
    }
  }

  const handleDeleteImage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this image?')) return;

    setIsLoading(true);
    try {
      await api.delete(`/admin/gallery-images/${id}`);
      toast.success('Gallery image deleted successfully!')
      setMessage('Image deleted successfully!');
      fetchGalleryImages(page);
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting image:', error);
      setMessage(error.response?.data?.message || 'Error deleting image');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="gallery-images">
      <div className="page-header">
        <h1>Gallery Images</h1>
        <p>Manage gallery images for your platform</p>
      </div>

      {/* Search was removed — gallery images have no name/title field to
          search by (see galleryImages.model.js), so a text search box here
          could never do anything useful. */}
      <div className="gallery-controls">
        <button
          className="upload-btn"
          disabled={totalItems >= MAX_GALLERY_UPLOAD}
          title={
            totalItems >= MAX_GALLERY_UPLOAD
              ? `Gallery already has the maximum of ${MAX_GALLERY_UPLOAD} images`
              : undefined
          }
          onClick={() => fileInputRef.current?.click()}
        >
          {totalItems >= MAX_GALLERY_UPLOAD
            ? `Maximum ${MAX_GALLERY_UPLOAD} images reached`
            : `+ Upload Images (${totalItems}/${MAX_GALLERY_UPLOAD})`}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleImageUpload}
          style={{ display: 'none' }}
        />
      </div>

      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}

      {isLoading ? (
        <p>Loading...</p>
      ) : (
        <div className="images-grid">
          {galleryImages.map(image => (
            <div key={image._id || image.id} className="image-card">
              <div className="image-container">
                <img src={`${API_IMAGE_BASE_URL}${image?.image}`} alt="image" />
                <button
                  className="delete-btn"
                  onClick={() => handleDeleteImage(image._id || image.id)}
                >
                  ×
                </button>
              </div>
              {/* Gallery images have no name/title field to show here (see
                  galleryImages.model.js) — kept as an empty spacer so card
                  spacing below the image is unchanged. */}
              <div className="image-info" />
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            disabled={page === 1}
            onClick={() => setPage(prev => prev - 1)}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            disabled={page === totalPages}
            onClick={() => setPage(prev => prev + 1)}
          >
            Next
          </button>
        </div>
      )}

      {galleryImages.length === 0 && !isLoading && (
        <div className="no-images">
          <p>No images uploaded yet.</p>
        </div>
      )}
      <ToastContainer
        toastClassName="custom-toast"
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
};


export default GalleryImages;
